import { useCallback, useEffect, useState } from 'react';

async function apiRequest(apiBase, path, options = {}) {
  const response = await fetch(`${apiBase}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
    ...options,
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.ok) {
    throw new Error(payload.message || 'Request failed');
  }
  return payload;
}

function CmsWorkflowPanel({ apiBase }) {
  const [queue, setQueue] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [newRequest, setNewRequest] = useState({
    content_id: '',
    change_summary: '',
    proposed_body: '',
    principal_reviewer_id: '',
    final_reviewer_id: '',
  });

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiRequest(apiBase, '/cms/workflow/queue');
      setQueue(res.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load workflow queue');
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  async function handleSubmitRequest(event) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await apiRequest(apiBase, '/cms/workflow/submit', {
        method: 'POST',
        body: JSON.stringify({
          content_id: Number(newRequest.content_id),
          change_summary: newRequest.change_summary,
          proposed_body: newRequest.proposed_body,
          principal_reviewer_id: Number(newRequest.principal_reviewer_id),
          final_reviewer_id: Number(newRequest.final_reviewer_id),
        }),
      });
      setNewRequest({
        content_id: '',
        change_summary: '',
        proposed_body: '',
        principal_reviewer_id: '',
        final_reviewer_id: '',
      });
      await loadQueue();
    } catch (err) {
      setError(err.message || 'Failed to submit change request');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDecision(requestId, decision) {
    setSubmitting(true);
    setError('');
    try {
      await apiRequest(apiBase, `/cms/workflow/review/${requestId}`, {
        method: 'POST',
        body: JSON.stringify({ decision, note: decision === 'approve' ? 'Approved' : 'Need revision' }),
      });
      await loadQueue();
    } catch (err) {
      setError(err.message || 'Failed to process review');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="super-admin-card">
      <h3>CMS Workflow Queue (3-Level Approval)</h3>
      {error && <div className="alert alert-error">{error}</div>}
      {loading && <div className="alert alert-info">Loading CMS workflow...</div>}

      <form className="super-admin-create-form" onSubmit={handleSubmitRequest}>
        <label>
          Content ID
          <input
            type="number"
            value={newRequest.content_id}
            onChange={(event) => setNewRequest((prev) => ({ ...prev, content_id: event.target.value }))}
            required
          />
        </label>
        <label>
          Change Summary
          <input
            type="text"
            value={newRequest.change_summary}
            onChange={(event) => setNewRequest((prev) => ({ ...prev, change_summary: event.target.value }))}
            required
          />
        </label>
        <label>
          Proposed Content Body
          <textarea
            className="super-admin-textarea"
            value={newRequest.proposed_body}
            onChange={(event) => setNewRequest((prev) => ({ ...prev, proposed_body: event.target.value }))}
            required
          />
        </label>
        <label>
          Principal Reviewer User ID
          <input
            type="number"
            value={newRequest.principal_reviewer_id}
            onChange={(event) => setNewRequest((prev) => ({ ...prev, principal_reviewer_id: event.target.value }))}
            required
          />
        </label>
        <label>
          Final Reviewer User ID
          <input
            type="number"
            value={newRequest.final_reviewer_id}
            onChange={(event) => setNewRequest((prev) => ({ ...prev, final_reviewer_id: event.target.value }))}
            required
          />
        </label>
        <button type="submit" disabled={submitting}>{submitting ? 'Submitting...' : 'Submit Change Request'}</button>
      </form>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Content</th>
              <th>Summary</th>
              <th>Stage</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {queue.map((item) => (
              <tr key={item.id}>
                <td>{item.id}</td>
                <td>{item.content_title}</td>
                <td>{item.change_summary}</td>
                <td>{item.current_stage}</td>
                <td>{item.workflow_status}</td>
                <td>
                  <button type="button" onClick={() => handleDecision(item.id, 'approve')} disabled={submitting}>Approve</button>
                  <button type="button" onClick={() => handleDecision(item.id, 'revise')} disabled={submitting}>Revise</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default CmsWorkflowPanel;
